import config from '@config/env.config';
import { BaseService, type NavigationFilters, type EntityListResponse, type StandardEntityParams } from './base-service';
import { PAGINATION } from './constants/service.constants';
import type { EntityFacets } from '@shared/types/status-filter.types';
import { PaymentLink } from '@shared/types/payment-links';
import { mockPaymentLinks } from '@shared/data/mockPaymentLinks';
import { FilteringUtils } from './utils/filtering.utils';

interface PaymentLinkServiceError extends Error {
  status?: number;
  code?: string;
}

interface PaymentLinkQueryParams extends StandardEntityParams {
  offset?: number;
  size?: number;
  facets?: string;
}

interface PaymentLinkListResponse extends EntityListResponse<PaymentLink> {
  size: number;
  results: PaymentLink[];
  page: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
  facets?: EntityFacets;
  offset?: number;
}

const DEFAULT_SORT = 'created';
const DEFAULT_FACETS = 'status';

class PaymentLinkService extends BaseService<PaymentLink, PaymentLinkListResponse, PaymentLinkQueryParams> {
  protected readonly entityName = 'payment-link';

  private readonly defaultOrgId: number;
  private readonly defaultAccountId: string;

  constructor() {
    super({
      baseURL: config.endpoints.merchants,
      apiKey: config.apiKey || '',
      entityName: 'payment-link',
    });
    this.defaultOrgId = 14791296;
    this.defaultAccountId = '1312312';
  }

  protected buildQueryString(params?: PaymentLinkQueryParams): string {
    if (!params) return '';

    const queryParams = new URLSearchParams();
    const pageSize = params.size || PAGINATION.DEFAULT_PAGE_SIZE;
    const offset = params.page !== undefined ? params.page * pageSize : params.offset || 0;

    queryParams.append('facets', params.facets || DEFAULT_FACETS);
    queryParams.append('sortby', params.sortBy || DEFAULT_SORT);
    queryParams.append('offset', offset.toString());
    queryParams.append('size', pageSize.toString());

    if (params.query?.trim()) {
      queryParams.append('query', params.query.trim());
    }

    const queryString = queryParams.toString();
    return queryString ? `?${queryString}` : '';
  }

  private buildUrl(orgId: number, merchantId: string, linkId?: string): string {
    const basePath = `organizations/${orgId}/accounts/${merchantId}/payment-links`;
    return linkId ? `${basePath}/${linkId}` : basePath;
  }

  private handleError(error: unknown, operation: string): never {
    const message = error instanceof Error ? error.message : 'Unknown error';
    const serviceError: PaymentLinkServiceError = new Error(`Failed to ${operation}: ${message}`);

    if (error && typeof error === 'object' && 'status' in error) {
      serviceError.status = error.status as number;
    }

    console.error(`PaymentLinkService.${operation}:`, error);
    throw serviceError;
  }

  async getPaymentLink(orgId: number, merchantId: string, linkId: string): Promise<PaymentLink> {
    const url = this.buildUrl(orgId, merchantId, linkId);

    try {
      return await this.apiClient.get<PaymentLink>(url, {
        headers: this.defaultHeaders,
      });
    } catch (error) {
      console.warn('[PaymentLinkService] API unavailable for getPaymentLink, falling back to mock data');
      const link = mockPaymentLinks.find((l) => l.id === linkId);
      if (!link) this.handleError(error, 'fetch payment link');
      return link!;
    }
  }

  async getPaymentLinks(params?: PaymentLinkQueryParams, orgId?: number, merchantId?: string): Promise<PaymentLinkListResponse> {
    const org = orgId || this.defaultOrgId;
    const account = merchantId || this.defaultAccountId;

    const queryString = this.buildQueryString(params);
    const url = `${this.buildUrl(org, account)}/all${queryString}`;

    try {
      return await this.apiClient.get<PaymentLinkListResponse>(url, {
        headers: this.defaultHeaders,
      });
    } catch (error) {
      console.warn('[PaymentLinkService] API unavailable, falling back to mock data:', error);
      return this.getMockPaymentLinks(params);
    }
  }

  private getMockPaymentLinks(params?: PaymentLinkQueryParams): PaymentLinkListResponse {
    const pageSize = params?.size || PAGINATION.DEFAULT_PAGE_SIZE;
    const page = params?.page || 0;
    const offset = params?.page !== undefined ? params.page * pageSize : params?.offset || 0;

    let filtered = [...mockPaymentLinks];

    if (params?.query?.trim()) {
      const q = params.query.trim().toLowerCase();
      // Handle status filter query format: //status=active/
      const statusMatch = q.match(/\/\/status=(\w+)\//);
      if (statusMatch) {
        filtered = filtered.filter((l) => l.status === statusMatch[1]);
      } else {
        filtered = filtered.filter(
          (l) =>
            l.name.toLowerCase().includes(q) ||
            l.id?.toLowerCase().includes(q) ||
            l.url?.toLowerCase().includes(q)
        );
      }
    }

    const totalElements = filtered.length;
    const totalPages = Math.ceil(totalElements / pageSize);
    const results = filtered.slice(offset, offset + pageSize);

    return {
      data: results,
      total: totalElements,
      size: pageSize,
      results,
      page,
      pageSize,
      totalElements,
      totalPages,
      hasNext: offset + pageSize < totalElements,
      hasPrevious: offset > 0,
      facets: FilteringUtils.calculateFacets(mockPaymentLinks, ['status', 'type']),
    };
  }

  async getEntityById(id: string): Promise<PaymentLink> {
    return this.getPaymentLink(this.defaultOrgId, this.defaultAccountId, id);
  }

  getEntitiesForNavigation = async (
    page: number,
    filters: NavigationFilters
  ): Promise<PaymentLinkListResponse> => {
    const params: PaymentLinkQueryParams = {
      page: page - 1,
      size: filters.pageSize || PAGINATION.DEFAULT_PAGE_SIZE,
    };

    if (filters.query?.trim()) {
      params.query = filters.query.trim();
    } else if (filters.statusFilter && filters.statusFilter !== 'all') {
      params.query = `//status=${filters.statusFilter}/`;
    }

    const response = await this.getPaymentLinks(params);
    if (!response) throw this.createServiceError('fetch', new Error('No response received from server'));

    return {
      ...response,
      data: response.results,
      total: response.totalElements,
    };
  }

  async createPaymentLink(orgId: number, merchantId: string, paymentLink: Partial<PaymentLink>): Promise<PaymentLink> {
    const url = this.buildUrl(orgId, merchantId);

    try {
      return await this.apiClient.post<PaymentLink>(url, paymentLink, {
        headers: this.defaultHeaders,
      });
    } catch (error) {
      console.warn('[PaymentLinkService] API unavailable for createPaymentLink, using mock response:', error);
      const mockId = `plink_${crypto.randomUUID()}`;
      return {
        id: mockId,
        name: paymentLink.name || 'Payment Link',
        status: 'active',
        type: paymentLink.type || 'fixed',
        currency: paymentLink.currency || 'USD',
        price: paymentLink.price || 0,
        created: new Date(),
        lastUpdated: new Date(),
        url: `https://pay.example.com/${mockId}`,
        views: 0,
        conversions: 0,
        totalRevenue: 0,
        revenue30d: 0,
        successRate: 0,
        ...paymentLink,
      } as PaymentLink;
    }
  }

  async updatePaymentLink(orgId: number, merchantId: string, linkId: string, paymentLink: Partial<PaymentLink>): Promise<PaymentLink> {
    const url = this.buildUrl(orgId, merchantId, linkId);

    try {
      return await this.apiClient.put<PaymentLink>(url, paymentLink, {
        headers: this.defaultHeaders,
      });
    } catch (error) {
      console.warn('[PaymentLinkService] API unavailable for updatePaymentLink, using mock response:', error);
      const existing = mockPaymentLinks.find((l) => l.id === linkId);
      return {
        ...(existing || {}),
        ...paymentLink,
        id: linkId,
        lastUpdated: new Date(),
      } as PaymentLink;
    }
  }

  async deletePaymentLink(orgId: number, merchantId: string, linkId: string): Promise<void> {
    const url = this.buildUrl(orgId, merchantId, linkId);

    try {
      await this.apiClient.delete<void>(url, {
        headers: this.defaultHeaders,
      });
    } catch (error) {
      this.handleError(error, 'delete payment link');
    }
  }
}

export const paymentLinkService = new PaymentLinkService();
